import { analytics } from '../firebase';

/**
 * Centralized logging utility for AR Drawing App
 * 
 * In development: Uses console.log/error for immediate feedback
 * In production: Uses Firebase Analytics for tracking (console calls are stripped by Vite)
 */

const isDevelopment = import.meta.env.DEV;

export const Logger = {
    /**
     * Debug logging - only visible in development
     * Automatically removed from production builds
     */
    debug: (...args: unknown[]) => {
        if (isDevelopment) {
            console.log('[DEBUG]', ...args);
        }
    },

    /**
     * Info logging - tracked in production via Analytics
     */
    info: (message: string, data?: Record<string, unknown>) => {
        if (isDevelopment) {
            console.log('[INFO]', message, data || '');
        } else {
            // Track as custom event in production
            analytics.logEvent('app_info', {
                message: message.substring(0, 100), // Limit message length
                ...data,
            }).catch(() => {
                // Silently fail if analytics not available
            });
        }
    },

    /**
     * Warning logging - tracked in production
     */
    warn: (message: string, ...args: unknown[]) => {
        if (isDevelopment) {
            console.warn('[WARN]', message, ...args);
        } else {
            analytics.logEvent('app_warning', {
                message: message.substring(0, 100),
            }).catch(() => {
                // Silently fail
            });
        }
    },

    /**
     * Error logging - tracked in production with stack traces
     */
    error: (message: string, error?: Error | unknown, data?: Record<string, unknown>) => {
        if (isDevelopment) {
            console.error('[ERROR]', message, error || '', data || '');
        } else {
            analytics.logEvent('app_error', {
                message: message.substring(0, 100),
                error_name: (error as Error)?.name || 'Unknown',
                error_message: (error as Error)?.message?.substring(0, 100) || '',
                stack: (error as Error)?.stack?.substring(0, 500) || '',
                ...data,
            }).catch(() => {
                // Silently fail
            });
        }
    },

    /**
     * Custom analytics event - works in both dev and production
     */
    event: (eventName: string, params?: Record<string, unknown>) => {
        if (isDevelopment) {
            console.log(`[EVENT] ${eventName}`, params || '');
        }

        analytics.logEvent(eventName, params).catch(() => {
            // Silently fail
        });
    },

    /**
     * Performance timing - useful for tracking slow operations
     */
    time: (label: string) => {
        if (isDevelopment) {
            console.time(label);
        }
    },

    timeEnd: (label: string) => {
        if (isDevelopment) {
            console.timeEnd(label);
        }
    },
};

export default Logger;
